import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { Icon } from '@/components/Icon'
import { useReservationsAddon } from '@/hooks/useReservationsAddon'

interface OpeningInterval {
  start: string
  end: string
}

type OpeningHours = Record<string, OpeningInterval[]>

interface Settings {
  opening_hours: OpeningHours
  closed_dates: string[]
  default_duration_minutes: number
  slot_interval_minutes: number
  min_advance_hours: number
  max_advance_days: number
  timezone: string
  max_capacity_per_slot: number | null
  notification_email: string | null
  notify_on_new_reservation: boolean
  accepting_reservations: boolean
  cancellation_policy: string | null
}

interface Resource {
  id: string
  name: string
  resource_type: 'table' | 'room' | 'staff' | 'service'
  capacity: number
  is_active: boolean
}

const WEEKDAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday']
const RESOURCE_TYPES = ['table', 'room', 'staff', 'service'] as const

const inputClass = 'w-full px-4 py-2 bg-[#1a1a25] border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#006cd4]'

export const ReservationSettings: React.FC = () => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const { visible, isLoading: addonLoading } = useReservationsAddon()

  const [form, setForm] = useState<Settings | null>(null)
  const [newClosedDate, setNewClosedDate] = useState('')
  const [saveState, setSaveState] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle')

  // Resources state
  const [resourceForm, setResourceForm] = useState({ name: '', resource_type: 'table' as Resource['resource_type'], capacity: 1 })
  const [resourceError, setResourceError] = useState('')

  const { data: settingsData, isLoading } = useQuery({
    queryKey: ['reservation-settings'],
    queryFn: async () => (await api.get('/reservations/settings')).data.data as Settings,
    enabled: visible,
  })

  const { data: resourcesData } = useQuery({
    queryKey: ['reservation-resources'],
    queryFn: async () => (await api.get('/reservations/resources?includeInactive=true')).data.data as Resource[],
    enabled: visible,
  })

  useEffect(() => {
    if (settingsData && !form) setForm({ ...settingsData })
  }, [settingsData, form])

  const resources = resourcesData || []

  const set = <K extends keyof Settings>(key: K, value: Settings[K]) =>
    setForm((prev) => (prev ? { ...prev, [key]: value } : prev))

  const setDayIntervals = (day: string, intervals: OpeningInterval[]) =>
    set('opening_hours', { ...(form?.opening_hours || {}), [day]: intervals })

  const saveSettings = async () => {
    if (!form) return
    setSaveState('saving')
    try {
      await api.put('/reservations/settings', {
        opening_hours: form.opening_hours,
        closed_dates: form.closed_dates,
        default_duration_minutes: form.default_duration_minutes,
        slot_interval_minutes: form.slot_interval_minutes,
        min_advance_hours: form.min_advance_hours,
        max_advance_days: form.max_advance_days,
        timezone: form.timezone,
        max_capacity_per_slot: form.max_capacity_per_slot,
        notification_email: form.notification_email || null,
        notify_on_new_reservation: form.notify_on_new_reservation,
        accepting_reservations: form.accepting_reservations,
        cancellation_policy: form.cancellation_policy || null,
      })
      setSaveState('saved')
      queryClient.invalidateQueries({ queryKey: ['reservation-settings'] })
      setTimeout(() => setSaveState('idle'), 3000)
    } catch {
      setSaveState('error')
    }
  }

  const addResource = async (e: React.FormEvent) => {
    e.preventDefault()
    setResourceError('')
    try {
      await api.post('/reservations/resources', resourceForm)
      setResourceForm({ name: '', resource_type: 'table', capacity: 1 })
      queryClient.invalidateQueries({ queryKey: ['reservation-resources'] })
    } catch (err: any) {
      setResourceError(err.response?.data?.error || t('reservationsSettings.resourceError'))
    }
  }

  const toggleResource = async (resource: Resource) => {
    await api.put(`/reservations/resources/${resource.id}`, { is_active: !resource.is_active })
    queryClient.invalidateQueries({ queryKey: ['reservation-resources'] })
  }

  const deleteResource = async (id: string) => {
    await api.delete(`/reservations/resources/${id}`)
    queryClient.invalidateQueries({ queryKey: ['reservation-resources'] })
  }

  if (!addonLoading && !visible) {
    return (
      <div className="bg-[#12121a] border border-white/10 rounded-xl p-8 text-center">
        <Icon name="lock" className="text-3xl text-gray-500 mb-4" />
        <h1 className="text-xl font-bold mb-2">{t('reservations.notAvailableTitle')}</h1>
        <p className="text-gray-400">{t('reservations.notAvailableDesc')}</p>
      </div>
    )
  }

  if (isLoading || !form) {
    return <div className="p-8 text-center text-gray-400">{t('reservations.loading')}</div>
  }

  return (
    <div className="max-w-4xl">
      <h1 className="text-3xl font-bold mb-8">{t('reservationsSettings.title')}</h1>

      {/* Master toggle */}
      <section className={`border rounded-xl p-6 mb-6 transition-colors ${form.accepting_reservations ? 'bg-green-500/5 border-green-500/20' : 'bg-red-500/5 border-red-500/20'}`}>
        <button
          type="button"
          onClick={() => set('accepting_reservations', !form.accepting_reservations)}
          className="w-full flex items-center gap-4 text-left"
        >
          <Icon
            name={form.accepting_reservations ? 'toggle-on' : 'toggle-off'}
            className={`text-3xl ${form.accepting_reservations ? 'text-green-400' : 'text-red-400'}`}
          />
          <span>
            <span className="block font-semibold">{t('reservationsSettings.accepting')}</span>
            <span className="block text-sm text-gray-400">{t('reservationsSettings.acceptingDesc')}</span>
          </span>
        </button>
      </section>

      {/* Opening hours */}
      <section className="bg-[#12121a] border border-white/10 rounded-xl p-6 mb-6">
        <h2 className="text-lg font-semibold mb-4">{t('reservationsSettings.openingHours')}</h2>
        <div className="space-y-3">
          {WEEKDAYS.map((day) => {
            const intervals = form.opening_hours[day] || []
            return (
              <div key={day} className="flex flex-col sm:flex-row sm:items-center gap-3">
                <span className="w-28 text-sm text-gray-400 capitalize">{t(`reservationsSettings.days.${day}`)}</span>
                <div className="flex-1 flex flex-wrap items-center gap-2">
                  {intervals.length === 0 && (
                    <span className="text-sm text-gray-600">{t('reservationsSettings.closed')}</span>
                  )}
                  {intervals.map((interval, i) => (
                    <div key={i} className="flex items-center gap-1 bg-[#1a1a25] border border-white/10 rounded-lg px-2 py-1">
                      <input
                        type="time"
                        value={interval.start}
                        onChange={(e) => {
                          const next = [...intervals]
                          next[i] = { ...interval, start: e.target.value }
                          setDayIntervals(day, next)
                        }}
                        className="bg-transparent text-white text-sm focus:outline-none"
                      />
                      <span className="text-gray-500">–</span>
                      <input
                        type="time"
                        value={interval.end}
                        onChange={(e) => {
                          const next = [...intervals]
                          next[i] = { ...interval, end: e.target.value }
                          setDayIntervals(day, next)
                        }}
                        className="bg-transparent text-white text-sm focus:outline-none"
                      />
                      <button
                        onClick={() => setDayIntervals(day, intervals.filter((_, j) => j !== i))}
                        className="p-1 text-gray-500 hover:text-red-400"
                        aria-label={t('common.delete')}
                      >
                        <Icon name="xmark" className="w-3" />
                      </button>
                    </div>
                  ))}
                  <button
                    onClick={() => setDayIntervals(day, [...intervals, { start: '13:00', end: '16:00' }])}
                    className="p-1.5 text-[#006cd4] hover:bg-[#006cd4]/10 rounded-lg transition-colors"
                    title={t('reservationsSettings.addInterval')}
                  >
                    <Icon name="plus" className="w-4" />
                  </button>
                </div>
              </div>
            )
          })}
        </div>
      </section>

      {/* Slot parameters */}
      <section className="bg-[#12121a] border border-white/10 rounded-xl p-6 mb-6">
        <h2 className="text-lg font-semibold mb-4">{t('reservationsSettings.slotParams')}</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <label className="block">
            <span className="text-sm text-gray-400">{t('reservationsSettings.duration')}</span>
            <input type="number" min={15} max={1440} value={form.default_duration_minutes} onChange={(e) => set('default_duration_minutes', parseInt(e.target.value, 10) || 90)} className={inputClass} />
          </label>
          <label className="block">
            <span className="text-sm text-gray-400">{t('reservationsSettings.slotInterval')}</span>
            <input type="number" min={5} max={240} value={form.slot_interval_minutes} onChange={(e) => set('slot_interval_minutes', parseInt(e.target.value, 10) || 30)} className={inputClass} />
          </label>
          <label className="block">
            <span className="text-sm text-gray-400">{t('reservationsSettings.minAdvance')}</span>
            <input type="number" min={0} max={720} value={form.min_advance_hours} onChange={(e) => set('min_advance_hours', parseInt(e.target.value, 10) || 0)} className={inputClass} />
          </label>
          <label className="block">
            <span className="text-sm text-gray-400">{t('reservationsSettings.maxAdvance')}</span>
            <input type="number" min={1} max={365} value={form.max_advance_days} onChange={(e) => set('max_advance_days', parseInt(e.target.value, 10) || 60)} className={inputClass} />
          </label>
          <label className="block">
            <span className="text-sm text-gray-400">{t('reservationsSettings.maxCapacity')}</span>
            <input
              type="number"
              min={1}
              value={form.max_capacity_per_slot ?? ''}
              placeholder={t('reservationsSettings.maxCapacityUnlimited')}
              onChange={(e) => set('max_capacity_per_slot', e.target.value === '' ? null : parseInt(e.target.value, 10))}
              className={inputClass}
            />
          </label>
          <label className="block">
            <span className="text-sm text-gray-400">{t('reservationsSettings.timezone')}</span>
            <input type="text" value={form.timezone} onChange={(e) => set('timezone', e.target.value)} placeholder="Europe/Madrid" className={inputClass} />
          </label>
        </div>
      </section>

      {/* Closed dates */}
      <section className="bg-[#12121a] border border-white/10 rounded-xl p-6 mb-6">
        <h2 className="text-lg font-semibold mb-4">{t('reservationsSettings.closedDates')}</h2>
        <div className="flex flex-wrap items-center gap-2 mb-3">
          {form.closed_dates.length === 0 && (
            <span className="text-sm text-gray-600">{t('reservationsSettings.noClosedDates')}</span>
          )}
          {form.closed_dates.map((date) => (
            <span key={date} className="inline-flex items-center gap-2 bg-[#1a1a25] border border-white/10 rounded-lg px-3 py-1 text-sm">
              {date}
              <button
                onClick={() => set('closed_dates', form.closed_dates.filter((d) => d !== date))}
                className="text-gray-500 hover:text-red-400"
                aria-label={t('common.delete')}
              >
                <Icon name="xmark" className="w-3" />
              </button>
            </span>
          ))}
        </div>
        <div className="flex gap-2">
          <input type="date" value={newClosedDate} onChange={(e) => setNewClosedDate(e.target.value)} className={inputClass} />
          <button
            onClick={() => {
              if (newClosedDate && !form.closed_dates.includes(newClosedDate)) {
                set('closed_dates', [...form.closed_dates, newClosedDate].sort())
                setNewClosedDate('')
              }
            }}
            className="px-4 py-2 bg-white/5 hover:bg-white/10 rounded-lg transition-colors whitespace-nowrap"
          >
            {t('reservationsSettings.addClosedDate')}
          </button>
        </div>
      </section>

      {/* Notifications + cancellation policy */}
      <section className="bg-[#12121a] border border-white/10 rounded-xl p-6 mb-6">
        <h2 className="text-lg font-semibold mb-4">{t('reservationsSettings.notifications')}</h2>
        <div className="space-y-4">
          <label className="flex items-center gap-3">
            <input
              type="checkbox"
              checked={form.notify_on_new_reservation}
              onChange={(e) => set('notify_on_new_reservation', e.target.checked)}
              className="rounded"
            />
            <span className="text-sm">{t('reservationsSettings.notifyOnNew')}</span>
          </label>
          <label className="block">
            <span className="text-sm text-gray-400">{t('reservationsSettings.notificationEmail')}</span>
            <input
              type="email"
              value={form.notification_email || ''}
              placeholder={t('reservationsSettings.notificationEmailPlaceholder')}
              onChange={(e) => set('notification_email', e.target.value || null)}
              className={inputClass}
            />
          </label>
          <label className="block">
            <span className="text-sm text-gray-400">{t('reservationsSettings.cancellationPolicy')}</span>
            <textarea
              value={form.cancellation_policy || ''}
              onChange={(e) => set('cancellation_policy', e.target.value || null)}
              rows={3}
              className={inputClass}
            />
          </label>
        </div>
      </section>

      <div className="flex items-center gap-4 mb-10">
        <button
          onClick={saveSettings}
          disabled={saveState === 'saving'}
          className="px-6 py-2 bg-[#006cd4] hover:bg-[#006cd4]/80 rounded-lg font-medium transition-colors disabled:opacity-50"
        >
          {saveState === 'saving' ? t('common.saving') : t('common.save')}
        </button>
        {saveState === 'saved' && <span className="text-green-400 text-sm">{t('reservationsSettings.saved')}</span>}
        {saveState === 'error' && <span className="text-red-400 text-sm">{t('reservationsSettings.saveError')}</span>}
      </div>

      {/* Resources */}
      <section className="bg-[#12121a] border border-white/10 rounded-xl p-6 mb-6">
        <h2 className="text-lg font-semibold mb-1">{t('reservationsSettings.resourcesTitle')}</h2>
        <p className="text-sm text-gray-500 mb-4">{t('reservationsSettings.resourcesDesc')}</p>

        {resources.length > 0 && (
          <div className="overflow-x-auto mb-4">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left px-3 py-2 text-sm font-medium text-gray-400">{t('reservationsSettings.resourceName')}</th>
                  <th className="text-left px-3 py-2 text-sm font-medium text-gray-400">{t('reservationsSettings.resourceType')}</th>
                  <th className="text-left px-3 py-2 text-sm font-medium text-gray-400">{t('reservationsSettings.resourceCapacity')}</th>
                  <th className="text-left px-3 py-2 text-sm font-medium text-gray-400">{t('common.status')}</th>
                  <th className="text-left px-3 py-2 text-sm font-medium text-gray-400">{t('reservations.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {resources.map((r) => (
                  <tr key={r.id} className="border-b border-white/5">
                    <td className="px-3 py-2 font-medium">{r.name}</td>
                    <td className="px-3 py-2 text-gray-300">{t(`reservationsSettings.resourceTypes.${r.resource_type}`)}</td>
                    <td className="px-3 py-2 text-gray-300">{r.capacity}</td>
                    <td className="px-3 py-2">
                      <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${r.is_active ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-400'}`}>
                        {r.is_active ? t('reservationsSettings.active') : t('reservationsSettings.inactive')}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex gap-1">
                        <button
                          onClick={() => toggleResource(r)}
                          title={r.is_active ? t('reservationsSettings.deactivate') : t('reservationsSettings.activate')}
                          className="p-2 text-gray-400 hover:bg-white/5 rounded-lg transition-colors"
                        >
                          <Icon name={r.is_active ? 'toggle-on' : 'toggle-off'} className="w-4" />
                        </button>
                        <button
                          onClick={() => deleteResource(r.id)}
                          title={t('common.delete')}
                          className="p-2 text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                        >
                          <Icon name="trash" className="w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <form onSubmit={addResource} className="flex flex-col sm:flex-row gap-3">
          <input
            required
            placeholder={t('reservationsSettings.resourceNamePlaceholder')}
            value={resourceForm.name}
            onChange={(e) => setResourceForm((prev) => ({ ...prev, name: e.target.value }))}
            className={inputClass}
          />
          <select
            value={resourceForm.resource_type}
            onChange={(e) => setResourceForm((prev) => ({ ...prev, resource_type: e.target.value as Resource['resource_type'] }))}
            className={inputClass}
          >
            {RESOURCE_TYPES.map((type) => (
              <option key={type} value={type}>{t(`reservationsSettings.resourceTypes.${type}`)}</option>
            ))}
          </select>
          <input
            type="number"
            min={1}
            max={1000}
            value={resourceForm.capacity}
            onChange={(e) => setResourceForm((prev) => ({ ...prev, capacity: parseInt(e.target.value, 10) || 1 }))}
            className={`${inputClass} sm:w-28`}
            title={t('reservationsSettings.resourceCapacity')}
          />
          <button type="submit" className="px-4 py-2 bg-[#006cd4] hover:bg-[#006cd4]/80 rounded-lg font-medium transition-colors whitespace-nowrap">
            {t('reservationsSettings.addResource')}
          </button>
        </form>
        {resourceError && <p className="text-red-400 text-sm mt-2">{resourceError}</p>}
      </section>
    </div>
  )
}
